import Bullhorn16 from '@carbon/icons-react/es/bullhorn/16';
import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import EarthFilled16 from '@carbon/icons-react/es/earth--filled/16';
import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import { Image, Skeleton, Tooltip, Typography } from 'antd';
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ProjectConversationId, ProjectId } from 'api/models';
import {
  ProjectConversation,
  ProjectGlobalConversation,
} from 'api/project-conversations';
import { ProjectGlobalConversationDocument } from 'api/project-conversations/global-conversation';
import { StatusFieldOption } from 'api/tasks';
import { RocketDocument16 } from 'components/atoms/icons';
import { Tag } from 'components/atoms/tag';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { useProjectIdFromUrl } from 'components/organisms/tasks/criteria/use-user-options';
import { useSelectedStatusOption } from 'data/fields/hooks';
import { BodyShort01, Label01 } from 'ozone/atoms';
import { useTaskDetailQuery } from 'queries/task';
import { abbreviateNumber } from 'utils/abbreviate-number';
import { unreachable } from 'utils/unreachable';
import {
  getProjectConversationDetailUrl,
  getProjectGlobalConversationDetailUrl,
} from 'utils/urls';
import { getGlobalConversationEntityMetaFromUrl } from '../../proj-conversation-hooks';

interface Props {
  conversation: ProjectConversation;
  projectId: ProjectId;
  realtimeUnreadCount: number;
}

const IconSkelton = styled(Skeleton)`
  width: var(--spacing-05);
  height: var(--spacing-05);
  display: block;
`;

const Wrapper = styled.div<{ active?: boolean; unread?: boolean }>`
  display: flex;
  align-items: center;
  border-right: 4px solid
    ${(props) => (props.active ? 'var(--primary-color)' : 'transparent')};
  background: ${(props) => (props.active ? 'var(--gray-10)' : 'transparent')};
  color: ${(props) =>
    props.active ? 'var(--primary-color)' : 'var(--gray-100)'};
  font-weight: ${(props) =>
    props.active || props.unread
      ? 'var(--font-weight-bold)'
      : 'var(--font-weight-normal)'};
  cursor: pointer;

  &:hover {
    background: var(--coolest-gray-10);
  }
  > span {
    color: ${(props) =>
      props.active ? 'var(--primary-color)' : 'var(--gray-100)'};
    font-weight: ${(props) =>
      props.active || props.unread
        ? 'var(--font-weight-bold)'
        : 'var(--font-weight-normal)'};
  }
`;

const Icon = styled(Label01)`
  padding: var(--spacing-03) var(--spacing-05) var(--spacing-03)
    var(--left-spacing);
  display: flex;
  align-items: center;
`;

const Body = styled(BodyShort01)`
  display: flex;
  justify-content: space-between;
  flex: 1;
  align-items: center;
  padding-right: var(--spacing-05);
  width: calc(100% - 56px);
`;

const Name = styled.div`
  width: calc(100% - 24px);
  span {
    width: 100%;
  }
`;

const COUNT_ABBREVIATE_TRIGGER = 999;

type ItemProps<T extends ProjectConversation | ProjectGlobalConversation> = {
  conversation: T;
  unreadCount: number;
  isActive: boolean;
  conversationName: string;
  url: string;
  Icon: FC | ReactElement;
};

const Item = <T extends ProjectConversation | ProjectGlobalConversation>({
  conversation,
  unreadCount,
  isActive,
  conversationName,
  url,
  Icon: SvgIcon,
}: ItemProps<T>) => {
  const color = conversation.private ? 'yellow' : 'blue';

  return (
    <Link to={url}>
      <Tooltip title={conversationName} mouseEnterDelay={0.5}>
        <Wrapper
          active={isActive}
          unread={unreadCount !== 0 && !isActive}
          data-cy={`other-conversation-${conversationName}`}
        >
          <Icon>{SvgIcon}</Icon>
          <Body>
            <Name>
              <Typography.Text ellipsis>{conversationName}</Typography.Text>
            </Name>
            {!!unreadCount && (
              <Tag color={color}>
                {unreadCount > COUNT_ABBREVIATE_TRIGGER
                  ? `${abbreviateNumber(unreadCount)}+`
                  : unreadCount}
              </Tag>
            )}
          </Body>
        </Wrapper>
      </Tooltip>
    </Link>
  );
};

export const ConversationItem: FC<Props> = ({
  conversation,
  projectId,
  realtimeUnreadCount,
}) => {
  const { chatId } =
    useParams<{
      chatId: string;
    }>();
  const isActive = conversation.conversationId === Number(chatId);
  const [unreadCount, setUnreadCount] = useState<number>(
    !isActive ? conversation.unreadMessages : 0,
  );
  const url = getProjectConversationDetailUrl(
    projectId,
    ProjectConversationId(conversation.conversationId),
  );

  useEffect(() => {
    if (typeof realtimeUnreadCount === 'number') {
      setUnreadCount(realtimeUnreadCount);
    }
  }, [realtimeUnreadCount]);

  return (
    <Item
      conversation={conversation}
      conversationName={conversation.conversationName}
      isActive={isActive}
      unreadCount={unreadCount}
      url={url}
      Icon={conversation.private ? <ViewOff16 /> : <EarthFilled16 />}
    />
  );
};

interface GlobalConversationProps {
  conversation: ProjectGlobalConversation;
  projectId: ProjectId;
}

const TaskConversationIcon: FC<{ taskId: number }> = ({ taskId }) => {
  const [, projectId] = useProjectIdFromUrl();

  const { data: task, isFetching } = useTaskDetailQuery(
    useMemo(() => ['task_id', projectId!, taskId], [projectId, taskId]),
  );
  const status = useSelectedStatusOption(task?.fields);

  if (isFetching) {
    return <IconSkelton avatar={{ size: 'small' }} paragraph={{ rows: 0 }} />;
  }
  if (!status) {
    return <IconSkelton avatar={{ size: 'small' }} paragraph={{ rows: 0 }} />;
  }

  if (!task) {
    return <ViewOff16 />;
  }

  return (
    <StatusIndicator
      className="status-indicator task-field-value"
      type={task.type}
      status={status as StatusFieldOption}
      progress={task.progress}
      locator={`status-indicator-${task.taskName}`}
    />
  );
};

const SpaceTabIcon: FC<{ conversation: ProjectGlobalConversationDocument }> = ({
  conversation,
}) => {
  if (conversation.spaceTab.metaData.icon) {
    return (
      <Image
        width={16}
        src={conversation.spaceTab.metaData.icon}
        alt="Document icon"
        preview={false}
      />
    );
  }

  return <RocketDocument16 />;
};

export const ConversationIcon: FC<{
  conversation: ProjectGlobalConversation;
  staticIcon: boolean;
}> = ({ conversation, staticIcon }) => {
  switch (conversation.displayEntityType) {
    case 'space-tabs':
      return (
        <SpaceTabIcon
          conversation={conversation as ProjectGlobalConversationDocument}
        />
      );
    case 'status-updates':
      return <Bullhorn16 />;
    case 'tasks':
      if (staticIcon) {
        return <CheckmarkOutline16 />;
      }

      return <TaskConversationIcon taskId={conversation.entityId} />;
    default:
      return unreachable(conversation as never);
  }
};

export const GlobalConversationItem: FC<GlobalConversationProps> = ({
  conversation,
  projectId,
}) => {
  const { chatId } =
    useParams<{
      chatId: string;
    }>();
  const { entityId, entityType } =
    getGlobalConversationEntityMetaFromUrl(chatId) || {};
  const isActive =
    Number.isInteger(entityId) &&
    conversation.entityId === Number(entityId) &&
    conversation.entityType === entityType;
  const unreadCount = conversation.unreadMessages;
  const url = getProjectGlobalConversationDetailUrl(
    projectId,
    conversation.entityId,
    conversation.entityType,
  );

  return (
    <Item
      conversation={conversation}
      conversationName={conversation.entityName}
      isActive={isActive}
      unreadCount={unreadCount}
      url={url}
      Icon={<ConversationIcon conversation={conversation} staticIcon />}
    />
  );
};
